import { useEffect, useState } from "react";
import { Box, Button, Group, Modal, SimpleGrid, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { createProviderFolder, renameProviderFolder } from "../../services/providerDocuments";
import useStyles from "./style";

/**
 * Create / rename modal for a provider document folder. Mirrors the staff and
 * client folder modals — pass `data` to rename that folder, omit it to create.
 *
 * @param {Object}   props
 * @param {String}   props.providerId     Provider the folder belongs to.
 * @param {Object}   [props.data]         Folder being renamed, if any.
 * @param {Object}   [props.parentFolder] Folder the new one is nested inside.
 * @param {Function} props.onSaved        Called after a successful save.
 */
const ProviderFolderModal = ({ opened, onClose, providerId, data, parentFolder, onSaved }) => {
  const { classes } = useStyles();
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: { name: data?.name || "" },
    validate: {
      name: (value) => (value?.trim() ? null : "Please specify a folder name"),
    },
  });

  // The modal stays mounted between openings, so the field has to be reset to
  // whatever folder it was opened for.
  useEffect(() => {
    if (opened) form.setValues({ name: data?.name || "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, data?._id]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSave = async (values) => {
    setSaving(true);
    const name = values.name.trim();
    const success = data
      ? await renameProviderFolder(providerId, data._id || data.id, name)
      : await createProviderFolder(providerId, name, parentFolder?._id || parentFolder?.id);
    setSaving(false);

    if (success) {
      onSaved?.();
      handleClose();
    }
  };

  return (
    <Modal
      className={classes.folderModal}
      opened={opened}
      size="lg"
      onClose={handleClose}
      shadow="md"
      overlayProps={{ blur: 3 }}
      radius="lg"
      zIndex={1000}
      centered
      title={
        <Box>
          <Text sx={{ fontFamily: "Inter", fontSize: 22, fontWeight: 600 }}>
            {data ? "Rename Folder" : "New Folder"}
          </Text>
          {!data && parentFolder && (
            <Text sx={{ fontFamily: "Inter", fontSize: 13, color: "#667085" }}>
              Inside “{parentFolder.name}”
            </Text>
          )}
        </Box>
      }
    >
      <form onSubmit={form.onSubmit(onSave)}>
        <SimpleGrid className={classes.folderModalBody}>
          <TextInput
            label="Name"
            size="md"
            placeholder="Folder Name"
            data-autofocus
            sx={{ fontFamily: "Inter" }}
            {...form.getInputProps("name")}
          />
        </SimpleGrid>

        <Group mt={20} grow>
          <Button
            color="gray"
            variant="outline"
            c="#000"
            fullWidth
            sx={{ fontFamily: "Inter" }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            c="#000"
            color="yellow"
            sx={{ fontFamily: "Inter" }}
            type="submit"
            loading={saving}
          >
            {data ? "Update" : "Create"}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default ProviderFolderModal;
